import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators'

import { URL_CONST } from 'utils/urlConsts'

import { addToast } from 'containers/Toasts/actions'

import {
  fetchTagsCanBeAddedError,
  fetchTagsCanBeAddedSuccess,
  fetchTagsCanBeRemovedError,
  fetchTagsCanBeRemovedSuccess,
  formSubmitError,
  formSubmitSuccess,
} from './actions'
import {
  FETCH_TAGS_CAN_BE_ADDED_ASYNC,
  FETCH_TAGS_CAN_BE_REMOVED_ASYNC,
  TAGS_FORM_SUBMIT_ASYNC,
  TYPE_OF_FORM,
} from './constants'

const formSubmitEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(TAGS_FORM_SUBMIT_ASYNC.INITIATE),
    mergeMap(() => {
      const state = state$.value
      const profileIds = Object.keys(state.mailboxes.management.selectedMailboxesMap)
      const tagsToInclude =
        state.mailboxes.tagsForm.typeOfForm === 1
          ? state.mailboxes.tagsForm.tagsCanBeAdded.items.filter((elem) => elem.type !== null)
          : state.mailboxes.tagsForm.tagsCanBeRemoved.items.filter((elem) => elem.type !== null)

      const tagsMap = tagsToInclude.reduce(
        (acc, elem) => (!acc ? { [elem.id]: elem.name } : { ...acc, [elem.id]: elem.name }),
        {}
      )

      const restOpt = {
        selectAll: state.mailboxes.management.selectAll,
        typeFilter: state.mailboxes.management.typeFilter,
        protectedFilter: state.mailboxes.management.protectedFilter,
        tagsFilter: state.mailboxes.management.selectAll
          ? state$.value.mailboxes.management.tagsFilter.map((elem) => elem.value).join(',')
          : '',
        segmentsFilter: state.mailboxes.management.selectAll
          ? state$.value.mailboxes.management.segmentsFilter.map((elem) => elem.value).join(',')
          : '',
        riskLevelsFilter: state.mailboxes.management.selectAll
          ? state$.value.mailboxes.management.riskLevelsFilter.map((elem) => elem.value).join(',')
          : '',
        search: state.mailboxes.management.search,
      }

      if (state.mailboxes.tagsForm.typeOfForm === TYPE_OF_FORM.ADD) {
        return api.mailboxes.assignTags(state.partnerOptions.selectedCompany?.value, {
          profileIds,
          tagNames: state.mailboxes.tagsForm.toAddTags.map(
            (elem) => tagsMap[elem.value] || elem.label
          ),
          ...restOpt,
        })
      }
      return api.mailboxes.removeTags(state.partnerOptions.selectedCompany?.value, {
        profileIds,
        tagNames: state.mailboxes.tagsForm.toRemoveTags.map(
          (elem) => tagsMap[elem.value] || elem.label
        ),
        ...restOpt,
      })
    }),
    mergeMap(({ response, status }) => {
      const actions = []

      if (status === 202) {
        actions.push(
          epicsErrorHandler(formSubmitError, {
            response: { message: 'Tag operation in progress. Please try again in a few seconds.' },
          })
        )
      } else {
        actions.push(formSubmitSuccess(response || {}))
        actions.push(
          addToast({
            message: 'Tags updated successfully!',
          })
        )
      }

      return of(...actions)
    }),
    catchError((error) => of(epicsErrorHandler(formSubmitError, error))),
    takeUntil(action$.pipe(ofType(TAGS_FORM_SUBMIT_ASYNC.CANCEL)))
  )

const fetchTagsCanBeAddedEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_TAGS_CAN_BE_ADDED_ASYNC.INITIATE),
    filter((action) => {
      if (action.type === FETCH_TAGS_CAN_BE_ADDED_ASYNC.SUCCESS)
        return window.location.href.indexOf(URL_CONST.mailboxes.management) !== -1
      return true
    }),
    mergeMap(() =>
      api.mailboxes
        .getTagsToModify({
          companyId: state$.value.partnerOptions.selectedCompany?.value,
          mode: 'add',
          page: state$.value.mailboxes.tagsForm.tagsCanBeAdded.page,
          search: state$.value.mailboxes.tagsForm.tagsCanBeAdded.search,
          allProfiles: state$.value.mailboxes.management.selectAll,
          profileIds: state$.value.mailboxes.management.selectAll
            ? []
            : Object.keys(state$.value.mailboxes.management.selectedMailboxesMap),
        })
        .pipe(
          map((response) => fetchTagsCanBeAddedSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchTagsCanBeAddedError, error))),
          takeUntil(action$.pipe(ofType(FETCH_TAGS_CAN_BE_ADDED_ASYNC.CANCEL)))
        )
    )
  )

const fetchTagsCanBeRemovedEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(FETCH_TAGS_CAN_BE_REMOVED_ASYNC.INITIATE),
    filter((action) => {
      if (action.type === FETCH_TAGS_CAN_BE_REMOVED_ASYNC.SUCCESS)
        return window.location.href.indexOf(URL_CONST.mailboxes.management) !== -1
      return true
    }),
    mergeMap(() =>
      api.mailboxes
        .getTagsToModify({
          companyId: state$.value.partnerOptions.selectedCompany?.value,
          mode: 'remove',
          page: state$.value.mailboxes.tagsForm.tagsCanBeRemoved.page,
          search: state$.value.mailboxes.tagsForm.tagsCanBeRemoved.search,
          allProfiles: state$.value.mailboxes.management.selectAll,
          profileIds: Object.keys(state$.value.mailboxes.management.selectedMailboxesMap),
        })
        .pipe(
          map((response) => fetchTagsCanBeRemovedSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchTagsCanBeRemovedError, error))),
          takeUntil(action$.pipe(ofType(FETCH_TAGS_CAN_BE_REMOVED_ASYNC.CANCEL)))
        )
    )
  )

export default combineEpics(formSubmitEpic, fetchTagsCanBeAddedEpic, fetchTagsCanBeRemovedEpic)
